/**
 * eBay Provider Adapter — Phase 4 Complete Engine
 *
 * Translates canonical Channel Listing data into eBay API payload shapes.
 * Consumes the provider-neutral publication projection.
 * Does NOT make live browser API calls — those go through Edge Functions.
 */

import type { ChannelListing, ChannelListingVariant } from "@/lib/channels";
import { resolveShippingFacts } from "@/lib/shipping-facts";
import { isProviderEligibleProductField, pickProductForProvider } from "./provider-outbound-allowlist";

// ---------------------------------------------------------------------------
// eBay Constants
// ---------------------------------------------------------------------------

export const EBAY_MARKETPLACES: Record<string, { id: string; name: string; currency: string; locale: string }> = {
  EBAY_AU: { id: "EBAY_AU", name: "Australia", currency: "AUD", locale: "en-AU" },
  EBAY_US: { id: "EBAY_US", name: "United States", currency: "USD", locale: "en-US" },
  EBAY_UK: { id: "EBAY_GB", name: "United Kingdom", currency: "GBP", locale: "en-GB" },
  EBAY_DE: { id: "EBAY_DE", name: "Germany", currency: "EUR", locale: "de-DE" },
  EBAY_FR: { id: "EBAY_FR", name: "France", currency: "EUR", locale: "fr-FR" },
  EBAY_IT: { id: "EBAY_IT", name: "Italy", currency: "EUR", locale: "it-IT" },
  EBAY_ES: { id: "EBAY_ES", name: "Spain", currency: "EUR", locale: "es-ES" },
  EBAY_CA: { id: "EBAY_CA", name: "Canada", currency: "CAD", locale: "en-CA" },
};

export const EBAY_CONDITION_MAP: Record<string, string> = {
  new: "1000",
  new_other: "1500",
  refurbished: "2000",
  used: "3000",
  very_good: "4000",
  good: "5000",
  acceptable: "6000",
  for_parts: "7000",
};

export const EBAY_OPTION_NAME_MAP: Record<string, string> = {
  Colour: "Color",
  Color: "Color",
  Size: "Size",
  Gender: "Gender",
  Material: "Material",
  Style: "Style",
  Pattern: "Pattern",
  Department: "Department",
  Length: "Length",
  Width: "Width",
  Height: "Height",
  Weight: "Weight",
};

export const EBAY_CAPABILITY_DEFAULTS = {
  listing_create: true,
  listing_update: true,
  listing_end: true,
  inventory_update: true,
  price_update: true,
  media_update: true,
  order_import: true,
  fulfilment_update: true,
  tracking_upload: true,
  category_lookup: true,
  aspect_lookup: true,
  policy_lookup: true,
  inventory_location_lookup: true,
  offer_publish: true,
  offer_revise: true,
  offer_end: true,
};

// ---------------------------------------------------------------------------
// Payload Builders
// ---------------------------------------------------------------------------

export interface EbayInventoryItemPayload {
  sku: string;
  locale: string;
  condition: string;
  conditionDescription?: string;
  availability: {
    shipToLocationAvailability: {
      quantity: number;
    };
  };
  product: {
    title: string;
    description: string;
    aspects: Record<string, string[]>;
    imageUrls: string[];
    brand?: string;
    mpn?: string;
    upc?: string;
    ean?: string;
    isbn?: string;
    gtin?: string;
  };
  packageWeightAndSize?: {
    weight?: { value: number; unit: string };
    dimensions?: { length: number; width: number; height: number; unit: string };
  };
}

export interface EbayItemGroupPayload {
  itemGroupType: string;
  title: string;
  description: string;
  imageUrls: string[];
  variantSKUs: string[];
  variationAspects: string[];
  aspects?: Record<string, string[]>;
}

export interface EbayOfferPayload {
  sku: string;
  marketplaceId: string;
  format: string;
  pricingSummary: {
    price: { value: string; currency: string };
  };
  quantity: number;
  quantityLimitPerBuyer?: number;
  listingPolicies: {
    fulfillmentPolicyId?: string;
    paymentPolicyId?: string;
    returnPolicyId?: string;
  };
  categoryId?: string;
  merchantLocationKey?: string;
  includeCatalogProductDetails?: boolean;
}

export interface EbayAspectMapping {
  aspectName: string;
  sourceType: "product_field" | "product_attribute" | "product_option" | "product_identifier" | "listing_override" | "fixed_value";
  sourceKey?: string;
  overrideValue?: string;
  isVariationAspect: boolean;
  resolvedValue?: string;
}

export interface EbayBusinessPolicy {
  policyType: "fulfillment" | "payment" | "return";
  externalPolicyId: string;
  policyName: string;
  isDefault: boolean;
}

export interface EbayInventoryLocation {
  merchantLocationKey: string;
  locationName?: string;
  location?: {
    address?: {
      addressLine1?: string;
      city?: string;
      stateOrProvince?: string;
      postalCode?: string;
      country?: string;
    };
    geoCoordinates?: {
      latitude?: number;
      longitude?: number;
    };
  };
  locationTypes?: string[];
  timeZoneId?: string;
}

// ---------------------------------------------------------------------------
// Description Safety
// ---------------------------------------------------------------------------

const ALLOWED_HTML_TAGS = new Set([
  "p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "table", "thead", "tbody", "tr", "td", "th", "div", "span", "a", "img", "blockquote",
]);

const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(["href"]),
  img: new Set(["src", "alt", "width", "height"]),
  table: new Set(["border", "cellpadding", "cellspacing", "width"]),
  td: new Set(["colspan", "rowspan", "width"]),
  th: new Set(["colspan", "rowspan", "width"]),
};

export function sanitizeEbayDescription(rawHtml: string): string {
  if (!rawHtml) return "";
  // Simple tag stripping approach for safety
  // In production, use DOMPurify or similar on the server
  const temp = document.createElement("div");
  temp.innerHTML = rawHtml;

  function cleanNode(node: Node): Node | null {
    if (node.nodeType === Node.TEXT_NODE) return node;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_HTML_TAGS.has(tag)) {
      const fragment = document.createDocumentFragment();
      while (el.firstChild) {
        const cleaned = cleanNode(el.firstChild);
        if (cleaned) fragment.appendChild(cleaned);
        else el.removeChild(el.firstChild);
      }
      return fragment;
    }

    const allowedAttrs = ALLOWED_ATTRIBUTES[tag];
    if (allowedAttrs) {
      Array.from(el.attributes).forEach((attr) => {
        if (!allowedAttrs.has(attr.name)) el.removeAttribute(attr.name);
      });
      if (tag === "a") {
        const href = el.getAttribute("href") || "";
        if (href.startsWith("javascript:") || href.startsWith("data:")) {
          el.removeAttribute("href");
        }
      }
    } else {
      Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));
    }

    const children = Array.from(el.childNodes);
    el.innerHTML = "";
    for (const child of children) {
      const cleaned = cleanNode(child);
      if (cleaned) el.appendChild(cleaned);
    }
    return el;
  }

  const result = cleanNode(temp);
  if (!result) return "";
  const wrapper = document.createElement("div");
  wrapper.appendChild(result);
  return wrapper.innerHTML;
}

// ---------------------------------------------------------------------------
// Inventory Item Builder
// ---------------------------------------------------------------------------

export function buildEbayInventoryItemPayload(params: {
  variant: ChannelListingVariant;
  listing: ChannelListing;
  product: { name: string; description: string | null; brand: string | null; mpn: string | null; condition: string | null; weight_kg?: number | null; length_cm?: number | null; width_cm?: number | null; height_cm?: number | null };
  imageUrls: string[];
  stockQty: number;
  optionMappings: Array<{ canonical_name: string; external_name: string }>;
  aspectMappings: EbayAspectMapping[];
  identifiers: Array<{ identifier_type: string; identifier_value: string; variant_id?: string | null }>;
  marketplaceId: string;
}): EbayInventoryItemPayload {
  const { variant, listing, imageUrls, stockQty, optionMappings, identifiers, marketplaceId } = params;
  const product = pickProductForProvider("ebay", params.product as unknown as Record<string, unknown>) as typeof params.product;
  const sku = variant.channel_sku || variant.variant?.sku || "";
  const locale = EBAY_MARKETPLACES[marketplaceId]?.locale || "en-AU";
  const conditionId = EBAY_CONDITION_MAP[(product.condition || "new").trim().toLowerCase()] ?? "1000";

  // Build aspects from option values
  const aspects: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(variant.variant?.option_values ?? {})) {
    const mapped = optionMappings.find((m) => m.canonical_name === k);
    const ebayName = mapped?.external_name || EBAY_OPTION_NAME_MAP[k] || k;
    aspects[ebayName] = [v];
  }
  if (product.brand) aspects["Brand"] = [product.brand];

  // Resolve identifiers
  const variantIdentifiers = identifiers.filter((id) => !id.variant_id || id.variant_id === variant.variant_id);
  const mpn = variantIdentifiers.find((id) => id.identifier_type === "mpn")?.identifier_value || product.mpn || undefined;
  const upc = variantIdentifiers.find((id) => id.identifier_type === "upc")?.identifier_value || undefined;
  const ean = variantIdentifiers.find((id) => id.identifier_type === "ean")?.identifier_value || undefined;
  const gtin = variantIdentifiers.find((id) => id.identifier_type === "gtin")?.identifier_value || undefined;
  const isbn = variantIdentifiers.find((id) => id.identifier_type === "isbn")?.identifier_value || undefined;

  const title = listing.channel_title || product.name;
  const description = sanitizeEbayDescription(listing.channel_description || product.description || title);

  const payload: EbayInventoryItemPayload = {
    sku,
    locale,
    condition: conditionId,
    availability: {
      shipToLocationAvailability: { quantity: Math.max(0, stockQty) },
    },
    product: {
      title,
      description,
      aspects,
      imageUrls: imageUrls.slice(0, 12),
      brand: product.brand || undefined,
      mpn,
      upc,
      ean,
      gtin,
      isbn,
    },
  };

  const shipping = resolveShippingFacts(
    {
      weightKg: variant.variant?.weight_kg,
      lengthCm: variant.variant?.length_cm,
      widthCm: variant.variant?.width_cm,
      heightCm: variant.variant?.height_cm,
    },
    {
      weightKg: product.weight_kg,
      lengthCm: product.length_cm,
      widthCm: product.width_cm,
      heightCm: product.height_cm,
    },
  );
  if (shipping.weightKg || shipping.lengthCm || shipping.widthCm || shipping.heightCm) {
    payload.packageWeightAndSize = {};
    if (shipping.weightKg) {
      payload.packageWeightAndSize.weight = { value: shipping.weightKg, unit: "KILOGRAM" };
    }
    if (shipping.lengthCm && shipping.widthCm && shipping.heightCm) {
      payload.packageWeightAndSize.dimensions = {
        length: shipping.lengthCm,
        width: shipping.widthCm,
        height: shipping.heightCm,
        unit: "CENTIMETER",
      };
    }
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Item Group Builder
// ---------------------------------------------------------------------------

export function buildEbayItemGroupPayload(params: {
  listing: ChannelListing;
  product: { name: string; description: string | null; brand: string | null };
  imageUrls: string[];
  variants: ChannelListingVariant[];
  optionMappings: Array<{ canonical_name: string; external_name: string }>;
}): EbayItemGroupPayload | null {
  const { listing, imageUrls, variants, optionMappings } = params;
  const product = pickProductForProvider("ebay", params.product as unknown as Record<string, unknown>) as typeof params.product;
  if (variants.length <= 1) return null; // Simple product, no group needed

  const variationAspects = optionMappings.map((m) => m.external_name || EBAY_OPTION_NAME_MAP[m.canonical_name] || m.canonical_name);

  const aspects: Record<string, string[]> = {};
  if (product.brand) aspects["Brand"] = [product.brand];

  return {
    itemGroupType: "ITEM_GROUP",
    title: listing.channel_title || product.name,
    description: sanitizeEbayDescription(listing.channel_description || product.description || ""),
    imageUrls: imageUrls.slice(0, 12),
    variantSKUs: variants.map((v) => v.channel_sku || v.variant?.sku || "").filter(Boolean),
    variationAspects,
    aspects,
  };
}

// ---------------------------------------------------------------------------
// Offer Builder
// ---------------------------------------------------------------------------

export function buildEbayOfferPayload(params: {
  variant: ChannelListingVariant;
  listing: ChannelListing;
  price: number;
  quantity: number;
  marketplaceId: string;
  policies: EbayBusinessPolicy[];
  categoryId?: string;
  merchantLocationKey?: string;
}): EbayOfferPayload {
  const { variant, listing, price, quantity, marketplaceId, policies, categoryId, merchantLocationKey } = params;
  const sku = variant.channel_sku || variant.variant?.sku || "";
  const currency = EBAY_MARKETPLACES[marketplaceId]?.currency || "AUD";

  const fulfillmentPolicy = policies.find((p) => p.policyType === "fulfillment" && p.isDefault)?.externalPolicyId;
  const paymentPolicy = policies.find((p) => p.policyType === "payment" && p.isDefault)?.externalPolicyId;
  const returnPolicy = policies.find((p) => p.policyType === "return" && p.isDefault)?.externalPolicyId;

  return {
    sku,
    marketplaceId,
    format: "FIXED_PRICE",
    pricingSummary: {
      price: { value: String(price.toFixed(2)), currency },
    },
    quantity: Math.max(0, quantity),
    listingPolicies: {
      fulfillmentPolicyId: fulfillmentPolicy,
      paymentPolicyId: paymentPolicy,
      returnPolicyId: returnPolicy,
    },
    categoryId,
    merchantLocationKey,
    includeCatalogProductDetails: false,
  };
}

// ---------------------------------------------------------------------------
// Variation Picture Builder
// ---------------------------------------------------------------------------

export function buildVariationPictures(params: {
  variants: ChannelListingVariant[];
  media: Array<{ role: string; source: string; storage_key: string | null; option_value_id?: string | null }>;
  optionName: string;
}): Array<{ optionName: string; optionValue: string; imageUrls: string[] }> {
  const { variants, media, optionName } = params;
  const pictures: Array<{ optionName: string; optionValue: string; imageUrls: string[] }> = [];

  // Group media by option value
  const mediaByValue = new Map<string, string[]>();
  for (const m of media) {
    if (m.source === "option_value" && m.option_value_id) {
      // Find the option value name
      for (const v of variants) {
        const optValues = v.variant?.option_values || {};
        if (Object.values(optValues).includes(m.option_value_id)) {
          const valueName = Object.entries(optValues).find(([, val]) => val === m.option_value_id)?.[1];
          if (valueName) {
            const existing = mediaByValue.get(valueName) || [];
            if (m.storage_key) existing.push(m.storage_key);
            mediaByValue.set(valueName, existing);
          }
        }
      }
    }
  }

  for (const [value, urls] of mediaByValue) {
    pictures.push({ optionName, optionValue: value, imageUrls: urls.slice(0, 12) });
  }

  return pictures;
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export interface ReconciliationEntry {
  field: string;
  sku?: string;
  internal: string | number | null;
  external: string | number | null;
  status: "match" | "drift" | "missing_external" | "missing_internal";
}

export function reconcileEbayListing(
  internalVariants: Array<{ sku: string; title: string; price: number; quantity: number }>,
  externalItems: Array<{ sku: string; title?: string; price?: number; quantity?: number }>,
): ReconciliationEntry[] {
  const results: ReconciliationEntry[] = [];

  for (const iv of internalVariants) {
    const ev = externalItems.find((e) => e.sku === iv.sku);

    if (!ev) {
      results.push({ field: "inventory_item", sku: iv.sku, internal: iv.sku, external: null, status: "missing_external" });
      continue;
    }

    // Title comparison
    results.push({
      field: "title",
      sku: iv.sku,
      internal: iv.title,
      external: ev.title ?? null,
      status: ev.title == null ? "missing_external" : iv.title === ev.title ? "match" : "drift",
    });

    // Price comparison
    results.push({
      field: "price",
      sku: iv.sku,
      internal: iv.price,
      external: ev.price ?? null,
      status: ev.price == null ? "missing_external" : Math.abs(iv.price - ev.price) < 0.01 ? "match" : "drift",
    });

    // Quantity comparison
    results.push({
      field: "quantity",
      sku: iv.sku,
      internal: iv.quantity,
      external: ev.quantity ?? null,
      status: ev.quantity == null ? "missing_external" : iv.quantity === ev.quantity ? "match" : "drift",
    });
  }

  // Check for external items not in internal
  for (const ev of externalItems) {
    if (!internalVariants.find((iv) => iv.sku === ev.sku)) {
      results.push({ field: "inventory_item", sku: ev.sku, internal: null, external: ev.sku, status: "missing_internal" });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Error Normalization
// ---------------------------------------------------------------------------

export interface NormalizedEbayError {
  code: string;
  message: string;
  severity: "blocking" | "warning" | "info";
  sku?: string;
  action: string;
  retryable: boolean;
  raw?: Record<string, unknown>;
}

export function normalizeEbayError(rawError: Record<string, unknown>): NormalizedEbayError {
  const errorCode = (rawError.errorCode as string) || (rawError.code as string) || "UNKNOWN";
  const message = (rawError.message as string) || (rawError.longMessage as string) || "Unknown eBay error";

  // Classify retryability
  const retryableCodes = new Set([
    "INTERNAL_ERROR", "SERVICE_UNAVAILABLE", "GATEWAY_TIMEOUT", "RATE_LIMIT_EXCEEDED",
    "THROTTLED", "NETWORK_ERROR", "TEMPORARY_ERROR",
  ]);

  // Classify severity
  const blockingCodes = new Set([
    "INVALID_SKU", "MISSING_REQUIRED_FIELD", "INVALID_CATEGORY", "INVALID_POLICY",
    "SKU_ALREADY_EXISTS", "INVALID_IDENTIFIER", "INVALID_ASPECT_VALUE",
  ]);

  const isRetryable = retryableCodes.has(errorCode);
  const isBlocking = blockingCodes.has(errorCode);

  return {
    code: errorCode,
    message,
    severity: isBlocking ? "blocking" : "warning",
    sku: (rawError.sku as string) || undefined,
    action: isRetryable
      ? "Retry after backoff"
      : isBlocking
      ? "Fix the underlying issue and retry"
      : "Review and resolve",
    retryable: isRetryable,
    raw: rawError,
  };
}

// ---------------------------------------------------------------------------
// Retry / Backoff
// ---------------------------------------------------------------------------

export function calculateBackoff(attempt: number, baseDelayMs = 1000, maxDelayMs = 30000): number {
  const delay = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs);
  // Add jitter (±20%)
  const jitter = delay * 0.2 * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(delay + jitter));
}

export function shouldRetry(errorCode: string, attempt: number, maxAttempts: number): boolean {
  if (attempt >= maxAttempts) return false;
  const retryable = new Set([
    "INTERNAL_ERROR", "SERVICE_UNAVAILABLE", "GATEWAY_TIMEOUT", "RATE_LIMIT_EXCEEDED",
    "THROTTLED", "NETWORK_ERROR", "TEMPORARY_ERROR", "TIMEOUT",
  ]);
  return retryable.has(errorCode);
}

// ---------------------------------------------------------------------------
// Idempotency
// ---------------------------------------------------------------------------

export function generateIdempotencyKey(listingId: string, version: number, operation: string): string {
  return `${listingId}:${version}:${operation}`;
}

export function generateSyncJobIdempotencyKey(listingId: string, jobType: string, version: number): string {
  return `sync:${listingId}:${jobType}:${version}`;
}

// ---------------------------------------------------------------------------
// Marketplace helpers
// ---------------------------------------------------------------------------

export function getMarketplaceName(marketplaceId: string): string {
  return EBAY_MARKETPLACES[marketplaceId]?.name || marketplaceId;
}

export function getMarketplaceCurrency(marketplaceId: string): string {
  return EBAY_MARKETPLACES[marketplaceId]?.currency || "USD";
}

export function getAllMarketplaces(): Array<{ id: string; name: string; currency: string }> {
  return Object.values(EBAY_MARKETPLACES);
}

// ---------------------------------------------------------------------------
// Category helpers
// ---------------------------------------------------------------------------

export function buildCategoryPath(category: { category_name: string; category_path?: string | null }): string {
  return category.category_path || category.category_name;
}

// ---------------------------------------------------------------------------
// Aspect helpers
// ---------------------------------------------------------------------------

export function resolveAspectValue(
  mapping: EbayAspectMapping,
  product: Record<string, unknown>,
  attributes: Array<{ attribute_name: string; value: string }>,
  options: Record<string, string>,
  identifiers: Array<{ identifier_type: string; identifier_value: string }>,
): string | null {
  switch (mapping.sourceType) {
    case "product_field":
      if (!isProviderEligibleProductField(mapping.sourceKey)) return null;
      return (product[mapping.sourceKey || ""] as string) || null;
    case "product_attribute":
      return attributes.find((a) => a.attribute_name === mapping.sourceKey)?.value || null;
    case "product_option":
      return options[mapping.sourceKey || ""] || null;
    case "product_identifier":
      return identifiers.find((i) => i.identifier_type === mapping.sourceKey)?.identifier_value || null;
    case "listing_override":
      return mapping.overrideValue || null;
    case "fixed_value":
      return mapping.overrideValue || null;
    default:
      return null;
  }
}